import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Wifi, WifiOff, RefreshCw, Cloud, CloudOff, 
  CheckCircle2, AlertCircle, Clock, Database 
} from 'lucide-react';

/**
 * Indicador de Status Online/Offline para o PDV
 */
export const OfflineIndicator = ({ 
  isOnline, 
  syncStatus, 
  onSync, 
  variant = 'compact' 
}) => {
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    if (!isOnline || syncing) return;
    
    setSyncing(true);
    try {
      await onSync?.();
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
    } finally {
      setSyncing(false);
    }
  };

  const formatLastSync = (dateString) => {
    if (!dateString) return 'Nunca';
    
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Agora';
    if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  // Variante compacta (para header)
  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2">
        {/* Status de conexão */}
        <div 
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
            isOnline 
              ? 'bg-emerald-100 text-emerald-700' 
              : 'bg-red-100 text-red-700 animate-pulse'
          }`}
          title={isOnline ? 'Conectado' : 'Modo Offline - Dados salvos localmente'}
        >
          {isOnline ? (
            <>
              <Wifi className="w-4 h-4" />
              <span className="hidden sm:inline">Online</span>
            </>
          ) : (
            <>
              <WifiOff className="w-4 h-4" />
              <span className="hidden sm:inline">Offline</span>
            </>
          )}
        </div>
        
        {/* Vendas pendentes */}
        {syncStatus?.pendingSales > 0 && (
          <div 
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-700"
            title={`${syncStatus.pendingSales} venda(s) aguardando sincronização`}
          >
            <Clock className="w-4 h-4" />
            <span>{syncStatus.pendingSales}</span>
          </div>
        )}
        
        {/* Botão de sync */}
        {isOnline && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSync}
            disabled={syncing}
            className="h-8 px-2"
            title="Sincronizar agora"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>
    );
  }

  // Variante detalhada (para página de configurações)
  return (
    <div className="bg-white rounded-xl border p-4 space-y-4">
      {/* Status Principal */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isOnline ? 'bg-emerald-100' : 'bg-red-100'
          }`}>
            {isOnline ? (
              <Cloud className="w-6 h-6 text-emerald-600" />
            ) : (
              <CloudOff className="w-6 h-6 text-red-600" />
            )}
          </div>
          <div>
            <h3 className={`font-semibold ${isOnline ? 'text-emerald-700' : 'text-red-700'}`}>
              {isOnline ? 'Conectado ao Servidor' : 'Modo Offline'}
            </h3>
            <p className="text-sm text-slate-500">
              {isOnline 
                ? 'Dados sincronizados em tempo real' 
                : 'Usando dados locais, vendas serão sincronizadas depois'}
            </p>
          </div>
        </div>
        
        {isOnline && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </Button>
        )}
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-50 rounded-lg p-3 text-center">
          <Database className="w-5 h-5 text-slate-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-slate-700">{syncStatus?.productsCount || 0}</p>
          <p className="text-xs text-slate-500">Produtos Locais</p>
        </div>
        
        <div className="bg-slate-50 rounded-lg p-3 text-center">
          <Clock className="w-5 h-5 text-amber-500 mx-auto mb-1" />
          <p className="text-lg font-bold text-amber-600">{syncStatus?.pendingSales || 0}</p>
          <p className="text-xs text-slate-500">Vendas Pendentes</p>
        </div>
        
        <div className="bg-slate-50 rounded-lg p-3 text-center col-span-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
          <p className="text-sm font-medium text-slate-700">{formatLastSync(syncStatus?.lastSync)}</p>
          <p className="text-xs text-slate-500">Última Sincronização</p>
        </div>
      </div>

      {/* Alertas */}
      {!isOnline && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Você está trabalhando offline</p>
            <p className="text-xs text-amber-600 mt-1">
              As vendas realizadas serão salvas localmente e sincronizadas automaticamente 
              quando a conexão for restabelecida.
            </p>
          </div>
        </div>
      )}

      {syncStatus?.pendingSales > 0 && isOnline && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
          <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">
              {syncStatus.pendingSales} venda(s) aguardando sincronização
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Clique em "Sincronizar" para enviar as vendas offline para o servidor.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
